/**
 * Sprint 11+12 — global keyboard shortcuts.
 *
 * Replaces the older `useShortcuts` hook with the wider shortcut surface
 * called for in the spec: G+letter navigation, single-key new-record actions,
 * `?` for help, and Escape for close.
 *
 * Suppressed when the user is typing in form fields, content-editable
 * surfaces, or any open modal (except for Escape, which always fires, and
 * Cmd/Ctrl+K, which is the always-on escape hatch).
 */
import { useEffect, useRef } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { useUIStore } from '@/stores/ui'

/** Where each `G + <key>` jumps to. Centralised so the help modal stays in sync. */
export const NAV_SHORTCUTS: Array<{ key: string; path: string; labelKey: string; defaultLabel: string }> = [
  { key: 'd', path: '/',              labelKey: 'shortcuts.goDashboard',     defaultLabel: 'Go to Dashboard' },
  { key: 'c', path: '/cases',         labelKey: 'shortcuts.goCases',         defaultLabel: 'Go to Cases' },
  { key: 'n', path: '/contacts',      labelKey: 'shortcuts.goContacts',      defaultLabel: 'Go to Contacts' },
  { key: 'l', path: '/leads',         labelKey: 'shortcuts.goLeads',         defaultLabel: 'Go to Leads' },
  { key: 'o', path: '/opportunities', labelKey: 'shortcuts.goOpps',          defaultLabel: 'Go to Opportunities' },
  { key: 'a', path: '/accounts',      labelKey: 'shortcuts.goAccounts',      defaultLabel: 'Go to Accounts' },
  { key: 'k', path: '/kb',            labelKey: 'shortcuts.goKB',            defaultLabel: 'Go to Knowledge base' },
  { key: 'r', path: '/reports',       labelKey: 'shortcuts.goReports',       defaultLabel: 'Go to Reports' },
  { key: 's', path: '/settings',      labelKey: 'shortcuts.goSettings',      defaultLabel: 'Go to Settings' },
  { key: 't', path: '/tasks',         labelKey: 'shortcuts.goTasks',         defaultLabel: 'Go to Tasks' },
]

/** Single-key creation actions. Each navigates to a list page with `?new=1`. */
export const NEW_SHORTCUTS: Array<{ key: string; path: string; labelKey: string; defaultLabel: string }> = [
  { key: 'c', path: '/cases?new=1',    labelKey: 'shortcuts.newCase',    defaultLabel: 'New case' },
  { key: 'n', path: '/contacts?new=1', labelKey: 'shortcuts.newContact', defaultLabel: 'New contact' },
  { key: 'l', path: '/leads?new=1',    labelKey: 'shortcuts.newLead',    defaultLabel: 'New lead' },
  { key: 't', path: '/tasks?new=1',    labelKey: 'shortcuts.newTask',    defaultLabel: 'New task' },
]

const G_TIMEOUT_MS = 1200

function isTyping(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false
  const tag = target.tagName
  if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return true
  // jsdom returns undefined for `isContentEditable` on plain elements; check
  // both the live property AND the attribute so the helper is portable.
  if (target.isContentEditable === true) return true
  return target.getAttribute('contenteditable') === 'true'
}

export function useKeyboardShortcuts() {
  const navigate = useNavigate()
  const location = useLocation()
  const toggleCommandPalette = useUIStore((s) => s.toggleCommandPalette)
  const closeCommandPalette = useUIStore((s) => s.closeCommandPalette)
  const openShortcutsHelp = useUIStore((s) => s.openShortcutsHelp)
  const closeShortcutsHelp = useUIStore((s) => s.closeShortcutsHelp)
  const commandPaletteOpen = useUIStore((s) => s.commandPaletteOpen)
  const shortcutsHelpOpen = useUIStore((s) => s.shortcutsHelpOpen)
  const gPressedAt = useRef<number>(0)

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      // Always-on escape hatches.
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault()
        toggleCommandPalette()
        return
      }
      if (e.key === 'Escape') {
        closeCommandPalette()
        closeShortcutsHelp()
        return
      }

      if (isTyping(e.target)) return
      // Only Escape should fire while a modal is open.
      if (commandPaletteOpen || shortcutsHelpOpen) return
      // Skip when the user is using a modifier (browser shortcuts win).
      if (e.metaKey || e.ctrlKey || e.altKey) return

      const k = e.key.toLowerCase()

      // `?` opens the shortcuts help (Shift+/ on most layouts).
      if (e.key === '?') {
        e.preventDefault()
        openShortcutsHelp()
        return
      }

      // `/` focuses global search.
      if (e.key === '/') {
        e.preventDefault()
        toggleCommandPalette()
        return
      }

      // Two-stroke `g` then <letter> navigation.
      if (k === 'g' && !gActive(gPressedAt.current)) {
        gPressedAt.current = Date.now()
        return
      }
      if (gActive(gPressedAt.current) && k !== 'g') {
        gPressedAt.current = 0
        const target = NAV_SHORTCUTS.find((n) => n.key === k)
        if (target) {
          e.preventDefault()
          navigate(target.path)
        }
        return
      }

      // Single-key creation. Only fire on root list pages so we don't hijack
      // typing-like behaviour inside detail views (which have their own
      // R/E/S/T bindings).
      const onListPage = ['/cases', '/contacts', '/leads', '/tasks'].includes(location.pathname)
      const isAtRoot   = !onListPage // create works from any non-list page (incl. dashboard)
      if (isAtRoot || onListPage) {
        const action = NEW_SHORTCUTS.find((n) => n.key === k)
        if (action) {
          e.preventDefault()
          navigate(action.path)
        }
      }
    }

    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [
    navigate, location.pathname,
    toggleCommandPalette, closeCommandPalette,
    openShortcutsHelp, closeShortcutsHelp,
    commandPaletteOpen, shortcutsHelpOpen,
  ])
}

function gActive(at: number): boolean {
  return at > 0 && Date.now() - at < G_TIMEOUT_MS
}

/** Exported for tests. */
export const _testing = { isTyping, gActive, G_TIMEOUT_MS }
